import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  return useContext(SubscriptionContext);
};

export const SubscriptionProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState({
    hasAccess: false,
    limits: {
      weeklyPosts: 7,
      aiModel: 'standard'
    }
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSubscriptionData = async () => {
      if (!currentUser) {
        setSubscriptionData({
          hasAccess: false,
          limits: {
            weeklyPosts: 7,
            aiModel: 'standard'
          }
        });
        setLoading(false);
        return;
      }

      try {
        // 1. Først tjekker vi direkte abonnement via Stripe API
        const response = await fetch(
          `${process.env.REACT_APP_STRIPE_CHECK_SUBSCRIPTION_URL}/${currentUser.uid}?email=${encodeURIComponent(currentUser.email)}`
        );
        
        if (!response.ok) {
          throw new Error(`Server svarede med status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Abonnementsdata fra Stripe:', data);
        
        // Hvis bruger allerede har direkte adgang via Stripe abonnement
        if (data.hasAccess) {
          setSubscriptionData(data);
          setError(null);
          setLoading(false);
          return;
        }
        
        // 2. Hvis ingen direkte adgang, så tjek for organisationslicens
        console.log('Tjekker for organisationslicens...');
        const hasOrgLicense = await checkOrganizationLicense(currentUser.uid);
        
        if (hasOrgLicense) {
          console.log('Bruger har licens via organisation');
          // Opdater subscriptionData med organisationslicens data
          setSubscriptionData({
            ...data,
            hasAccess: true,
            orgLicense: true,
            limits: {
              weeklyPosts: 7, // Standard begrænsninger for organisationsmedlemmer
              aiModel: 'standard'
            }
          });
        } else {
          // Ingen licens fundet, brug data fra Stripe API
          setSubscriptionData(data);
        }
        
        setError(null);
      } catch (error) {
        console.error('Fejl ved hentning af abonnementsdata:', error);
        setError(error.message);
        setSubscriptionData({
          hasAccess: false,
          limits: {
            weeklyPosts: 7,
            aiModel: 'standard'
          }
        });
      } finally {
        setLoading(false);
      }
    };

    // Tjek om brugeren har en organisationslicens tildelt
    const checkOrganizationLicense = async (userId) => {
      try {
        console.log(`Tjekker organisationslicenser for bruger: ${userId}`);
        
        // Hent brugerens dokument direkte
        const userDocRef = doc(db, 'users', userId);
        const userDocSnapshot = await getDoc(userDocRef);
        
        if (!userDocSnapshot.exists()) {
          console.log('Ingen brugerdata fundet');
          return false;
        }
        
        const userData = userDocSnapshot.data();
        const userOrgs = userData.organizations || [];
        
        console.log(`Bruger tilhører ${userOrgs.length} organisationer:`, userOrgs);
        
        if (userOrgs.length === 0) {
          console.log('Bruger er ikke medlem af nogen organisationer');
          return false;
        }
        
        // Tjek hver organisation om brugeren har en tildelt licens
        for (const orgId of userOrgs) {
          console.log(`Tjekker licenser i organisation: ${orgId}`);
          
          // Tjek om bruger har en direkte tildelt licens
          const licenseQuery = query(
            collection(db, 'organizations', orgId, 'licenses'),
            where('assignedTo', '==', userId)
          );
          
          const licenseSnapshot = await getDocs(licenseQuery);
          
          if (!licenseSnapshot.empty) {
            console.log(`Licens fundet i organisation ${orgId} tildelt til brugeren`);
            return true;
          }
          
          // Tjek om bruger er aktivt medlem i organisationen
          const memberRef = doc(db, 'organizations', orgId, 'members', userId);
          const memberSnapshot = await getDoc(memberRef);
          
          if (memberSnapshot.exists() && memberSnapshot.data().status === 'active') {
            console.log(`Bruger er aktivt medlem i organisation ${orgId}`);
            
            // Tjek om organisationen har aktive licenser
            const orgRef = doc(db, 'organizations', orgId);
            const orgSnapshot = await getDoc(orgRef);
            
            if (orgSnapshot.exists()) {
              const orgData = orgSnapshot.data();
              if (orgData.status === 'active' || orgData.status === 'trialing') {
                console.log(`Organisation ${orgId} har aktiv status: ${orgData.status}`);
                return true;
              }
            }
          }
        }
        
        console.log('Ingen aktive licenser eller medlemskaber fundet for brugeren');
        return false;
      } catch (error) {
        console.error('Fejl ved tjek af organisationslicens:', error);
        return false;
      }
    };

    fetchSubscriptionData();
  }, [currentUser]);

  // Funktion til at tjekke om brugeren kan oprette flere opslag denne uge
  const canCreateMorePosts = async (currentWeekPostsCount) => {
    if (!subscriptionData?.hasAccess) return false;
    
    const weeklyLimit = subscriptionData.limits.weeklyPosts;
    return currentWeekPostsCount < weeklyLimit;
  };

  // Funktion til at få antallet af resterende opslag for denne uge
  const getRemainingWeeklyPosts = (currentWeekPostsCount) => {
    if (!subscriptionData?.hasAccess) return 0;
    
    const weeklyLimit = subscriptionData.limits.weeklyPosts;
    return Math.max(0, weeklyLimit - currentWeekPostsCount);
  };

  const value = {
    subscriptionData,
    loading,
    error,
    canCreateMorePosts,
    getRemainingWeeklyPosts,
    weeklyPostsLimit: subscriptionData?.limits?.weeklyPosts || 0,
    aiModel: subscriptionData?.limits?.aiModel || 'none',
    totalSeats: subscriptionData?.totalSeats || 0,
    hasAccess: subscriptionData?.hasAccess || false
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}; 